import axios from "axios";
import Config from "../_config/config";

export interface RubricScoringStats {
  totalUnscoredSubmissions: number;
  unscoredWithRubrics: number;
  recentlyGradedCount: number;
}

export interface RubricScoringResult {
  totalProcessed: number;
  successfullyScored: number;
  skipped: number;
  errors: number;
  details: Array<{
    submissionId: number;
    status: string;
    score?: number;
    maxScore?: number;
    grade?: string;
    percentageScore?: string;
    reason?: string;
    error?: string;
  }>;
}

export interface SingleSubmissionEvaluationResult {
  submissionId: number;
  score: number;
  maxScore: number;
  percentageScore: number;
  grade: string;
  feedback: string;
  criteriaScores: Array<{
    criterionId: string;
    criterionName: string;
    score: number;
    maxScore: number;
    feedback: string;
  }>;
}

/**
 * Service for interacting with rubric scoring API endpoints
 */
class RubricScoringService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${Config.nodeSiteUrl}v1/rubric-scoring`;
  }

  /**
   * Get auth headers with Bearer token
   */
  private getAuthHeaders(): any {
    const headers: any = {
      'Content-Type': 'application/json',
    };

    const currentUser = localStorage.getItem('currentUser');
    console.log('[RubricScoringService] currentUser from localStorage:', currentUser);

    if (currentUser && currentUser !== 'undefined') {
      try {
        const user = JSON.parse(currentUser);
        console.log('[RubricScoringService] Parsed user:', user);
        const token = user.token;
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          console.log('[RubricScoringService] Added Authorization header');
        } else {
          console.warn('[RubricScoringService] No token found in currentUser');
        }
      } catch (error) {
        console.error('[RubricScoringService] Failed to parse currentUser from localStorage:', error);
      }
    } else {
      console.warn('[RubricScoringService] No currentUser in localStorage');
    }

    return headers;
  }

  /**
   * Trigger scan and evaluate all unscored submissions
   */
  async scanAndEvaluateSubmissions(): Promise<{
    success: boolean;
    message?: string;
    data?: RubricScoringResult;
    error?: string;
  }> {
    try {
      const url = `${this.baseUrl}/scan-and-evaluate`;
      const response = await axios.post(url, {}, {
        headers: this.getAuthHeaders()
      });
      return response.data as any;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to evaluate submissions",
        error: error.response?.data?.error || error.message,
      };
    }
  }

  /**
   * Get rubric scoring statistics
   */
  async getScoringStats(): Promise<{
    success: boolean;
    data?: RubricScoringStats;
    error?: string;
  }> {
    try {
      const url = `${this.baseUrl}/stats`;
      const headers = this.getAuthHeaders();

      console.log('[RubricScoringService] getScoringStats URL:', url);
      console.log('[RubricScoringService] Headers:', headers);
      console.log('[RubricScoringService] Authorization header:', headers['Authorization']);

      const response = await axios.get(url, {
        headers: headers
      });
      return response.data as any;
    } catch (error: any) {
      console.error('[RubricScoringService] Error:', error.response?.data);
      console.error('[RubricScoringService] Full error:', error);

      if (error.response?.status === 401) {
        return {
          success: false,
          error: "Authentication failed. Please log in again.",
        };
      }

      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.message || "Failed to fetch statistics",
      };
    }
  }

  /**
   * Evaluate a single submission by ID
   */
  async evaluateSingleSubmission(
    submissionId: number
  ): Promise<{
    success: boolean;
    message?: string;
    data?: SingleSubmissionEvaluationResult;
    error?: string;
  }> {
    try {
      const url = `${this.baseUrl}/evaluate/${submissionId}`;
      const response = await axios.post(url, {}, {
        headers: this.getAuthHeaders()
      });
      return response.data as any;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to evaluate submission",
        error: error.response?.data?.error || error.message,
      };
    }
  }
}

export default new RubricScoringService();
